import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Users, Search, Shield, MoreVertical, Trash2, CheckCircle } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';
import { badgeAPI, rolesV5API } from '../../lib/api';

// All 17 implemented roles with display labels + colour coding
const ALL_ROLES = [
  { id: 'super_admin',          label: 'Super Admin',           color: 'bg-purple-100 text-purple-800' },
  { id: 'admin',                label: 'Administrator',         color: 'bg-red-100 text-red-800' },
  { id: 'editor',               label: 'Editor',                color: 'bg-blue-100 text-blue-800' },
  { id: 'producer',             label: 'Producer',              color: 'bg-cyan-100 text-cyan-800' },
  { id: 'journalist',           label: 'Journalist / Reporter', color: 'bg-emerald-100 text-emerald-800' },
  { id: 'photographer',         label: 'Photographer',          color: 'bg-green-100 text-green-800' },
  { id: 'designer',             label: 'Designer',              color: 'bg-pink-100 text-pink-800' },
  { id: 'data_journalist',      label: 'Data Journalist',       color: 'bg-violet-100 text-violet-800' },
  { id: 'social_media_manager', label: 'Social Media Manager',  color: 'bg-sky-100 text-sky-800' },
  { id: 'seo_specialist',       label: 'SEO Specialist',        color: 'bg-orange-100 text-orange-800' },
  { id: 'ad_operations',        label: 'Ad Operations',         color: 'bg-yellow-100 text-yellow-800' },
  { id: 'subscription_manager', label: 'Subscription Manager',  color: 'bg-teal-100 text-teal-800' },
  { id: 'analytics_analyst',    label: 'Analytics Analyst',     color: 'bg-indigo-100 text-indigo-800' },
  { id: 'hr_admin',             label: 'HR Administrator',      color: 'bg-lime-100 text-lime-800' },
  { id: 'finance_controller',   label: 'Finance Controller',    color: 'bg-amber-100 text-amber-800' },
  { id: 'legal_counsel',        label: 'Legal Counsel',         color: 'bg-rose-100 text-rose-800' },
  { id: 'external_contributor', label: 'External Contributor',  color: 'bg-gray-100 text-gray-700' },
  { id: 'moderator',            label: 'Moderator',             color: 'bg-fuchsia-100 text-fuchsia-800' },
  { id: 'reader',               label: 'Reader',                color: 'bg-slate-100 text-slate-700' },
];

const ROLE_MAP = Object.fromEntries(ALL_ROLES.map(r => [r.id, r]));

function RoleBadge({ role }) {
  const r = ROLE_MAP[role] || { label: role, color: 'bg-gray-100 text-gray-700' };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.color}`}>{r.label}</span>;
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadUsers(); }, [page]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const res = await usersAPI.list(params);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const handleRoleChange = async () => {
    if (!editUser || !editRole) return;
    try {
      await usersAPI.update(editUser.id, { role: editRole });
      toast.success(`Role updated to ${editRole.replace('_', ' ')}`);
      setEditUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await usersAPI.update(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      loadUsers();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await usersAPI.delete(id);
      toast.success('User deleted');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-sm text-[#5B616E] mt-1">{total} total users</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-9 pr-4 py-2 border border-[#E7E0D6] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#B89B5E] text-sm"
        />
      </form>

      {/* Role Change Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>Update role for {editUser?.full_name} ({editUser?.email})</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <Select value={editRole} onValueChange={setEditRole}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {ALL_ROLES.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.color}`}>●</span>
                      {r.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {editRole && (
              <div className="bg-[#FBF7EF] border border-[#E7E0D6] rounded-xl p-3">
                <p className="text-xs font-medium mb-1">Selected: <RoleBadge role={editRole} /></p>
                <p className="text-[10px] text-[#5B616E]">
                  {ROLE_MAP[editRole]?.label} — role-specific permissions apply immediately after save.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button className="bg-[#121316] text-white hover:bg-[#1B1D22]" onClick={handleRoleChange}>Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="space-y-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : (
        <Card className="bg-white border-[#E7E0D6] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E7E0D6] bg-[#FBF7EF]">
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3">User</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3">Role</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden sm:table-cell">Status</th>
                  <th className="text-left text-xs font-medium text-[#5B616E] px-4 py-3 hidden md:table-cell">Joined</th>
                  <th className="text-right text-xs font-medium text-[#5B616E] px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-[#E7E0D6] hover:bg-[#FBF7EF]/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#E7E0D6] flex items-center justify-center text-xs font-semibold">
                            {u.full_name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium">{u.full_name}</p>
                          <p className="text-xs text-[#5B616E]">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        <RoleBadge role={u.role} />
                        {u.is_verified && <CheckCircle className="w-3.5 h-3.5 text-blue-500" title="Verified" />}
                        {u.mfa_enabled && <Shield className="w-3.5 h-3.5 text-green-600" title="MFA enabled" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <Badge variant={u.is_active ? 'default' : 'secondary'} className="text-[10px]">{u.is_active ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-[#5B616E]">{new Date(u.created_at).toLocaleDateString()}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 hover:bg-[#E7E0D6] rounded-lg"><MoreVertical className="w-4 h-4" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setEditUser(u); setEditRole(u.role); }}>
                            <Shield className="w-4 h-4 mr-2" /> Change Role
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(u)}>
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={async () => {
                            try {
                              await badgeAPI.setVerified(u.id, !u.is_verified);
                              toast.success(u.is_verified ? 'Badge removed' : 'User verified ✓');
                              loadUsers();
                            } catch { toast.error('Failed'); }
                          }}>
                            <CheckCircle className="w-4 h-4 mr-2" /> {u.is_verified ? 'Remove badge' : 'Verify user'}
                          </DropdownMenuItem>
                          {u.id !== currentUser?.id && (
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(u.id)}>
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                page === i + 1 ? 'bg-[#121316] text-white' : 'bg-white border border-[#E7E0D6] hover:bg-[#E7E0D6]'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
